import { KeyRound, LogOut, Menu } from 'lucide-react';
import { Suspense, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { NAV_GROUPS, NAV_ITEMS } from '@/app/navigation';
import { PageLoader } from '@/components/ui';
import { ChangePasswordDialog } from '@/features/auth/ChangePasswordDialog';
import { authApi } from '@/features/auth/api';
import { cn } from '@/lib/cn';
import { useAuthStore } from '@/stores/auth.store';
import { queryClient } from '@/app/query-client';

export function AppShell() {
  const user = useAuthStore((s) => s.user)!;
  const signOut = useAuthStore((s) => s.signOut);
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);

  const items = NAV_ITEMS.filter((i) => i.visible(user));
  const current = items.find((i) => (i.to === '/' ? location.pathname === '/' : location.pathname.startsWith(i.to)));

  const logout = async () => {
    await authApi.logout().catch(() => undefined);
    queryClient.clear();
    signOut();
    navigate('/login', { replace: true });
  };

  const nav = (
    <nav className="flex-1 overflow-y-auto">
      {(Object.keys(NAV_GROUPS) as (keyof typeof NAV_GROUPS)[]).map((group) => {
        const groupItems = items.filter((i) => i.group === group);
        if (!groupItems.length) return null;
        return (
          <div key={group}>
            <div className="px-3 pt-3 pb-1.5 text-[11px] text-navy-300 max-[1100px]:hidden">{NAV_GROUPS[group]}</div>
            {groupItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  cn(
                    'my-0.5 flex items-center gap-3 rounded-[10px] px-3 py-2 text-[13px] text-[#afc0d2] transition hover:bg-navy-600 hover:text-white max-[1100px]:justify-center',
                    isActive && 'border-e-[3px] border-[#4fd6bb] bg-navy-600 text-white',
                  )
                }
                title={item.label}
              >
                <item.icon className="size-[18px] shrink-0" />
                <span className="max-[1100px]:hidden">{item.label}</span>
              </NavLink>
            ))}
          </div>
        );
      })}
    </nav>
  );

  const brand = (
    <div className="flex items-center gap-3 px-2 pb-6">
      <div className="grid size-10 shrink-0 place-items-center rounded-[13px] bg-gradient-to-br from-[#1bbaa4] to-[#4088dd] text-xl font-black text-white">م</div>
      <div className="max-[1100px]:hidden">
        <b className="block text-lg text-white">مدار</b>
        <small className="text-[10px] text-[#a8b9ce]">إدارة التوزيع والمبيعات</small>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen">
      {/* Desktop / tablet sidebar */}
      <aside className="sticky top-0 hidden h-screen w-[246px] shrink-0 flex-col bg-navy px-4 py-6 md:flex max-[1100px]:w-[76px] max-[1100px]:px-2.5">
        {brand}
        {nav}
        <div className="border-t border-[#35485f] px-2 pt-4 text-[11px] leading-6 text-[#a6bbd0] max-[1100px]:hidden">
          {user.name}
          <br />
          {user.roleLabel}
        </div>
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-navy/50" onClick={() => setMobileOpen(false)} />
          <aside className="absolute inset-y-0 start-0 flex w-72 flex-col bg-navy px-4 py-6 [&_.max-\[1100px\]\:hidden]:!block [&_a]:!justify-start">
            {brand}
            {nav}
          </aside>
        </div>
      )}

      <div className="min-w-0 flex-1">
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between gap-4 border-b border-line bg-white/95 px-4 backdrop-blur md:h-[74px] md:px-8">
          <div className="flex items-center gap-3">
            <button className="rounded-lg p-2 hover:bg-soft md:hidden" onClick={() => setMobileOpen(true)} aria-label="القائمة">
              <Menu className="size-5" />
            </button>
            <div className="text-xs text-muted">
              النظام / <b className="text-ink">{current?.label ?? ''}</b>
            </div>
          </div>
          <div className="relative">
            <button onClick={() => setMenuOpen((v) => !v)} className="flex items-center gap-2.5 rounded-xl p-1 hover:bg-soft">
              <span className="hidden text-end text-xs sm:block">
                <b className="block">{user.name}</b>
                <span className="text-muted">{user.roleLabel}</span>
              </span>
              <span className="grid size-9 place-items-center rounded-full bg-[#d9e8f8] font-bold text-[#2764a9]">{user.name.slice(0, 1)}</span>
            </button>
            {menuOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
                <div className="card absolute end-0 z-20 mt-2 w-52 overflow-hidden p-1.5">
                  <button onClick={() => { setMenuOpen(false); setPwdOpen(true); }} className="flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-sm hover:bg-soft">
                    <KeyRound className="size-4" /> تغيير كلمة المرور
                  </button>
                  <button onClick={logout} className="flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-sm text-danger hover:bg-danger-50">
                    <LogOut className="size-4" /> تسجيل الخروج
                  </button>
                </div>
              </>
            )}
          </div>
        </header>
        <main className="mx-auto max-w-[1540px] px-3.5 py-6 md:px-8 md:pb-12">
          <Suspense fallback={<PageLoader />}>
            <Outlet />
          </Suspense>
        </main>
      </div>
      <ChangePasswordDialog open={pwdOpen} onClose={() => setPwdOpen(false)} />
    </div>
  );
}
